import { useEffect, useState } from 'react';
import { api, type AdminUserRow } from '@/api/client';
import { ErrorBox, Field, Loading, Modal, PageTitle, fullW } from '@/components/ui';

export function UsersPage() {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ total: number; items: AdminUserRow[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminUserRow | null>(null);
  const [loading, setLoading] = useState(false);

  const load = (currentPage = page, currentQ = q) => {
    setLoading(true);
    api
      .users(currentQ, currentPage, 25)
      .then((r) => setData({ total: r.total, items: r.items }))
      .catch((e) => setErr(String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(page, q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (page === 1) load(1, q);
      else setPage(1);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <div>
      <PageTitle title="Players" />

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search email, username, name or id…"
          style={{ ...fullW }}
        />
      </div>

      {err && <ErrorBox text={err} />}
      {loading && !data && <Loading />}
      {data && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data">
            <thead>
              <tr>
                <th>Player</th>
                <th>Email / Username</th>
                <th style={{ textAlign: 'right' }}>MMR</th>
                <th style={{ textAlign: 'right' }}>Coins</th>
                <th style={{ textAlign: 'right' }}>Gems</th>
                <th style={{ textAlign: 'right' }}>W/L</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((u) => (
                <tr key={u.id} onClick={() => setEditing(u)}>
                  <td>
                    <div style={{ fontWeight: 600 }}>
                      {u.displayName}
                      {u.role === 'ADMIN' && <span className="badge badge-admin" style={{ marginLeft: 6 }}>ADMIN</span>}
                      {u.role === 'MODERATOR' && <span className="badge badge-mod" style={{ marginLeft: 6 }}>MOD</span>}
                      {u.isBanned && <span className="badge badge-ban" style={{ marginLeft: 6 }}>BANNED</span>}
                      {u.isGuest && <span className="badge badge-guest" style={{ marginLeft: 6 }}>GUEST</span>}
                    </div>
                    <div
                      className="mono"
                      style={{ fontSize: 10, color: 'var(--ink-dim)', marginTop: 2 }}
                    >
                      {u.id}
                    </div>
                  </td>
                  <td style={{ color: 'var(--ink-muted)' }}>
                    {u.email ?? '—'}
                    {u.username && (
                      <div style={{ color: 'var(--ink-dim)', fontSize: 11 }}>
                        @{u.username}
                      </div>
                    )}
                  </td>
                  <td className="mono" style={{ textAlign: 'right', color: 'var(--c-cyan)' }}>{u.mmr}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>{u.coins.toLocaleString()}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>{u.gems.toLocaleString()}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>
                    {u.matchesWon}/{u.matchesPlayed}
                  </td>
                  <td style={{ color: 'var(--ink-muted)', fontSize: 11 }}>
                    {new Date(u.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.items.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-muted)', fontSize: 13 }}>
              No players match.
            </div>
          )}
        </div>
      )}

      {data && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 12,
            fontSize: 12,
            color: 'var(--ink-muted)',
          }}
        >
          <span>{data.total.toLocaleString()} total</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              className="btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Prev
            </button>
            <span style={{ padding: '6px 8px' }}>page {page}</span>
            <button
              className="btn"
              disabled={data.items.length < 25}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {editing && (
        <UserEdit
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={(u) => {
            setEditing(null);
            setData((d) =>
              d ? { ...d, items: d.items.map((x) => (x.id === u.id ? u : x)) } : d,
            );
          }}
        />
      )}
    </div>
  );
}

function UserEdit({
  user,
  onClose,
  onSaved,
}: {
  user: AdminUserRow;
  onClose: () => void;
  onSaved: (u: AdminUserRow) => void;
}) {
  const [displayName, setDisplayName] = useState(user.displayName);
  const [role, setRole] = useState(user.role);
  const [mmr, setMmr] = useState(user.mmr);
  const [coins, setCoins] = useState(user.coins);
  const [gems, setGems] = useState(user.gems);
  const [isBanned, setIsBanned] = useState(user.isBanned);
  const [bannedReason, setBannedReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const { user: u } = await api.patchUser(user.id, {
        displayName,
        role,
        mmr,
        coins,
        gems,
        isBanned,
        ...(isBanned && bannedReason ? { bannedReason } : {}),
      });
      onSaved(u);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Edit · ${user.displayName}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <Field label="DISPLAY NAME">
        <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </Field>
      <Field label="ROLE">
        <select value={role} onChange={(e) => setRole(e.target.value as AdminUserRow['role'])}>
          <option value="USER">USER</option>
          <option value="MODERATOR">MODERATOR</option>
          <option value="ADMIN">ADMIN</option>
        </select>
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <Field label="MMR">
          <input type="number" value={mmr} onChange={(e) => setMmr(+e.target.value || 0)} />
        </Field>
        <Field label="COINS">
          <input type="number" value={coins} onChange={(e) => setCoins(+e.target.value || 0)} />
        </Field>
        <Field label="GEMS">
          <input type="number" value={gems} onChange={(e) => setGems(+e.target.value || 0)} />
        </Field>
      </div>
      <Field label="BANNED">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={isBanned} onChange={(e) => setIsBanned(e.target.checked)} />
          <span style={{ fontSize: 13 }}>Account is banned from playing</span>
        </label>
      </Field>
      {isBanned && (
        <Field label="REASON (recorded in audit log)">
          <input value={bannedReason} onChange={(e) => setBannedReason(e.target.value)} />
        </Field>
      )}
      {err && <ErrorBox text={err} />}
    </Modal>
  );
}
